import { useEffect, useRef } from "react";

// Adds .is-visible to every [data-reveal] inside the returned ref once it
// enters the viewport. IntersectionObserver rather than a scroll listener
// (Section 5.D), and it disconnects as soon as everything has fired.
export default function useReveal({ threshold = 0.2 } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    const targets = root.matches("[data-reveal]")
      ? [root, ...root.querySelectorAll("[data-reveal]")]
      : [...root.querySelectorAll("[data-reveal]")];
    if (!targets.length) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      targets.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    let remaining = targets.length;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          io.unobserve(entry.target);
          if (--remaining === 0) io.disconnect();
        });
      },
      { threshold, rootMargin: "0px 0px -8% 0px" }
    );

    targets.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [threshold]);

  return ref;
}
